import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import type { ToastPosition, ToastVariant } from './types';

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideInTop = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -12px);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
`;

const slideInBottom = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, 12px);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
`;

type StyledToastProps = {
  $variant: ToastVariant;
  $position: ToastPosition;
  $embedded: boolean;
};

const VARIANT_FALLBACK: Record<ToastVariant, string> = {
  success: '#2f806a',
  error: '#b42b2b',
  info: '#085490',
  warning: '#fbbf24',
};

function getPositionCss(position: ToastPosition): string {
  switch (position) {
    case 'bottom-right':
      return `
        top: auto;
        bottom: 24px;
        right: 24px;
        left: auto;
      `;
    case 'bottom-center':
      return `
        top: auto;
        bottom: 24px;
        left: 50%;
        right: auto;
        transform: translateX(-50%);
      `;
    case 'top-center':
      return `
        top: 24px;
        left: 50%;
        right: auto;
        bottom: auto;
        transform: translateX(-50%);
      `;
    case 'top-right':
    default:
      return `
        top: 24px;
        right: 24px;
        left: auto;
        bottom: auto;
      `;
  }
}

function getAnimation(position: ToastPosition) {
  if (position === 'top-center') return slideInTop;
  if (position === 'bottom-center') return slideInBottom;
  return slideInRight;
}

export const StyledToast = styled.div<StyledToastProps>`
  position: ${({ $embedded }) => ($embedded ? 'relative' : 'fixed')};
  z-index: ${({ theme, $embedded }) =>
    $embedded ? 'auto' : (theme?.zIndex?.toast ?? 60)};
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme?.component?.toast?.gap ?? '10px'};
  max-width: min(400px, calc(100vw - 48px));
  width: ${({ $embedded }) => ($embedded ? '100%' : 'auto')};
  padding: ${({ theme }) => theme?.component?.toast?.padding ?? '16px'};
  background: ${({ theme }) =>
    theme?.component?.toast?.backgroundColor ?? '#ffffff'};
  border-radius: ${({ theme }) =>
    theme?.component?.toast?.borderRadius ?? '8px'};
  box-shadow: ${({ theme }) =>
    theme?.component?.toast?.shadow ?? '0 4px 12px rgba(0, 0, 0, 0.15)'};
  border-left: 4px solid
    ${({ theme, $variant }) =>
      theme?.component?.toast?.variants?.[$variant]?.accentColor ??
      VARIANT_FALLBACK[$variant]};
  box-sizing: border-box;
  animation: ${({ $position, $embedded }) =>
      $embedded ? slideInRight : getAnimation($position)}
    0.25s ease-out;

  ${({ $position, $embedded }) => ($embedded ? '' : getPositionCss($position))}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  @media (forced-colors: active) {
    border: 1px solid ButtonText;
  }
`;

export const IconWrapper = styled.span<{ $variant: ToastVariant }>`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  color: ${({ theme, $variant }) =>
    theme?.component?.toast?.variants?.[$variant]?.iconColor ??
    ($variant === 'warning' ? '#92400e' : VARIANT_FALLBACK[$variant])};
`;

export const ToastMessage = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 0.875rem;
  line-height: 1.375rem;
  color: ${({ theme }) => theme?.colors?.text?.primary ?? '#292929'};
`;

export const DismissButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-height: 32px;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme?.colors?.text?.secondary ?? '#808080'};
  cursor: pointer;
  line-height: 0;

  &:hover {
    color: ${({ theme }) => theme?.colors?.text?.primary ?? '#292929'};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme?.colors?.text?.primary ?? '#292929'};
    outline-offset: 2px;
  }

  @media (forced-colors: active) {
    outline: 1px solid ButtonText;
  }
`;
